import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useNavigate } from "react-router-dom";

const partnerTypes = [
  { icon: "ri-building-4-line", title: "School Districts", desc: "Curriculum integration and pilot program partnerships across K–12 institutions." },
  { icon: "ri-heart-line", title: "Nonprofits", desc: "Community financial education program development and co-delivery." },
  { icon: "ri-government-line", title: "Policy Organizations", desc: "State mandate alignment, advocacy support, and legislative briefings." },
  { icon: "ri-microscope-line", title: "Research Institutions", desc: "Longitudinal outcome studies, data collaboration, and academic publishing." },
  { icon: "ri-briefcase-4-line", title: "Workforce Programs", desc: "Bridging academic financial literacy with real-world workforce readiness." },
  { icon: "ri-global-line", title: "Foundations & Funders", desc: "Philanthropic investment in scalable national education reform." },
];

const badges = ["Framework Development", "Pilot Structuring", "Policy Integration", "Research Collaboration", "Institutional Partnerships"];

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function FounderPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const id = "jsonld-founder";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "name": "Aditi Sanjay Kumar – Founder of CashLearn",
        "url": `${SITE_URL}/founder`,
        "description": "Meet Aditi Sanjay Kumar, founder of CashLearn — a financial literacy education initiative building research-backed frameworks for K–12 students and institutional partners.",
        "isPartOf": { "@type": "WebSite", "url": SITE_URL },
        "dateModified": "2026-04-14",
        "mainEntity": {
          "@type": "Person",
          "name": "Aditi Sanjay Kumar",
          "jobTitle": "Founder",
          "worksFor": {
            "@type": "EducationalOrganization",
            "name": "CashLearn",
            "url": SITE_URL
          },
          "description": "Aditi Sanjay Kumar founded CashLearn on the belief that economic preparedness is a fundamental educational right. She develops institutional partnerships, research collaborations, and pilot implementations for financial literacy education.",
          "sameAs": [
            "https://www.linkedin.com/in/aditi-cc/",
            "https://www.instagram.com/aditi.sanjay.kumar/"
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Aditi Sanjay Kumar – Founder | CashLearn",
        "url": `${SITE_URL}/founder`,
        "description": "Meet Aditi Sanjay Kumar, founder of CashLearn — a financial literacy education initiative building research-backed frameworks for K–12 students and institutional partners.",
        "isPartOf": { "@type": "WebSite", "url": SITE_URL },
        "dateModified": "2026-04-14",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "Founder", "item": `${SITE_URL}/founder` }
          ]
        }
      }
    ]);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Navbar />

      {/* Quote + Story */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-[#475569] hover:text-[#1e3a5f] text-sm mb-12 transition-colors cursor-pointer whitespace-nowrap font-['Inter']"
          >
            <i className="ri-arrow-left-line" /> Back to Home
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-medium tracking-widest uppercase text-[#3b82f6] font-['Inter']">The Founder</span>
              <blockquote className="border-l-2 border-[#1e3a5f] pl-6 mt-6 mb-8">
                <p className="font-['Playfair_Display'] text-xl md:text-2xl italic text-[#1e3a5f] leading-relaxed">
                  &ldquo;Every student deserves to graduate with the financial infrastructure knowledge to navigate the real economic systems they&apos;ll face — not just the theory, but the practical architecture of money, credit, and wealth.&rdquo;
                </p>
              </blockquote>
              <p className="text-[#475569] text-sm md:text-base leading-relaxed font-['Inter'] mb-5">
                Cash Learn was founded on the belief that economic preparedness is a fundamental educational right — not a privilege. The initiative emerged from years of observing the downstream consequences of financial illiteracy across workforce, housing, and wealth-building outcomes for American youth.
              </p>
              <p className="text-[#475569] text-sm md:text-base leading-relaxed font-['Inter'] mb-8">
                Cash Learn is actively developing institutional partnerships, research collaborations, and pilot implementations. We welcome school districts, nonprofits, policy organizations, and workforce development programs to engage during this foundational phase.
              </p>

            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[460px]">
                <img
                  src="https://static.readdy.ai/image/a3e8b0182dc4ba81eda0b98b43a24071/64d847e7d30750c4f4e567879ef39362.jpeg"
                  alt="Cash Learn Founder"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/30 to-transparent rounded-3xl" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#1e3a5f] rounded-2xl p-5 w-48">
                <div className="text-white/60 text-xs font-['Inter'] mb-3">Aditi Sanjay Kumar</div>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.linkedin.com/in/aditi-cc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
                  >
                    <i className="ri-linkedin-fill text-white text-sm" />
                  </a>
                  <a
                    href="https://www.instagram.com/aditi.sanjay.kumar/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-white text-sm" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 md:py-28 bg-[#f8faff]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs font-medium tracking-widest uppercase text-[#3b82f6] font-['Inter']">Who We Work With</span>
            <h2 className="font-['DM_Serif_Display'] text-4xl md:text-5xl text-[#0f172a] mt-3">Partnership Opportunities</h2>
            <p className="text-[#475569] text-sm max-w-xl mx-auto mt-4 font-['Inter'] leading-relaxed">
              We&apos;re building a coalition of institutions committed to transforming economic literacy education at the national level.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {partnerTypes.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-white border border-[#dbeafe] rounded-2xl hover:border-[#3b82f6]/50 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#eff6ff] flex-shrink-0">
                  <i className={`${item.icon} text-[#1d4ed8] text-lg`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#0f172a] font-['Inter'] mb-1">{item.title}</div>
                  <div className="text-xs text-[#475569] leading-relaxed font-['Inter']">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1e3a5f]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-medium tracking-widest uppercase text-[#93c5fd] font-['Inter']">Get Involved</span>
          <h2 className="font-['DM_Serif_Display'] text-4xl md:text-5xl text-white mt-4 mb-6">
            Ready to Build<br /><em>Something Lasting?</em>
          </h2>
          <p className="text-white/60 text-sm md:text-base leading-relaxed font-['Inter'] mb-10 max-w-xl mx-auto">
            Whether you represent a school district, a research institution, or a policy organization — we want to hear from you. This is a foundational moment, and the right partners matter.
          </p>
          <button
            onClick={() => { navigate("/"); setTimeout(() => { const el = document.getElementById("contact"); if (el) { const top = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top, behavior: "smooth" }); } }, 300); }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1e3a5f] hover:bg-[#eff6ff] rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap font-['Inter']"
          >
            Request Institutional Briefing <i className="ri-arrow-right-line" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
